import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Search } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { toast } from 'sonner';
import { productsAPI } from '../../services/api';
import { ImageUpload } from '../ui/image-upload';
import { RichTextEditor } from '../ui/rich-text-editor';

const ProductsTab = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({ name: '', category: '', description: '', image: '' });
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      const response = await productsAPI.getAll();
      setProducts(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei prodotti');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!productForm.image) {
      toast.error('Carica un\'immagine per il prodotto');
      return;
    }
    try {
      if (editingProduct) {
        await productsAPI.update(editingProduct.id, productForm);
        toast.success('Prodotto aggiornato!');
      } else {
        await productsAPI.create(productForm);
        toast.success('Prodotto creato!');
      }
      loadProducts();
      setIsDialogOpen(false);
      setEditingProduct(null);
      setProductForm({ name: '', category: '', description: '', image: '' });
    } catch (error) {
      toast.error('Errore nel salvare il prodotto');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo prodotto?')) return;
    try {
      await productsAPI.delete(id);
      toast.success('Prodotto eliminato!');
      loadProducts();
    } catch (error) {
      toast.error('Errore nell\'eliminare il prodotto');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      category: product.category,
      description: product.description,
      image: product.image
    });
    setIsDialogOpen(true);
  };

  const filteredProducts = products.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Card className="glass-card">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-4 sm:space-y-0">
          <div>
            <CardTitle className="text-2xl">Gestione Prodotti</CardTitle>
            <CardDescription>Aggiungi, modifica o elimina prodotti</CardDescription>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                data-testid="add-product-btn"
                onClick={() => { 
                  setEditingProduct(null); 
                  setProductForm({ name: '', category: '', description: '', image: '' }); 
                }}
              >
                <Plus className="w-4 h-4 mr-2" />Aggiungi Prodotto
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingProduct ? 'Modifica Prodotto' : 'Nuovo Prodotto'}</DialogTitle>
                <DialogDescription>{editingProduct ? 'Aggiorna le informazioni' : 'Compila i dettagli'}</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">Nome Prodotto</Label>
                  <Input 
                    id="name" 
                    data-testid="product-name-input"
                    value={productForm.name} 
                    onChange={(e) => setProductForm({ ...productForm, name: e.target.value })} 
                    required 
                    className="mt-2" 
                  />
                </div>
                <div>
                  <Label htmlFor="category">Categoria</Label>
                  <Input 
                    id="category" 
                    data-testid="product-category-input"
                    value={productForm.category} 
                    onChange={(e) => setProductForm({ ...productForm, category: e.target.value })} 
                    required 
                    className="mt-2" 
                  />
                </div>
                <div>
                  <Label htmlFor="description">Descrizione</Label>
                  <div className="mt-2">
                    <RichTextEditor
                      value={productForm.description}
                      onChange={(value) => setProductForm({ ...productForm, description: value })}
                      placeholder="Descrizione del prodotto..."
                      minHeight="150px"
                    />
                  </div>
                </div>
                <div>
                  <Label>Immagine Prodotto</Label>
                  <div className="mt-2">
                    <ImageUpload
                      value={productForm.image}
                      onChange={(url) => setProductForm({ ...productForm, image: url })}
                      uploadType="image"
                      label="Carica immagine prodotto"
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Annulla</Button>
                  <Button type="submit" data-testid="submit-product-btn">{editingProduct ? 'Aggiorna' : 'Crea'} Prodotto</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
          <Input 
            type="text" 
            placeholder="Cerca prodotti..." 
            value={searchTerm} 
            onChange={(e) => setSearchTerm(e.target.value)} 
            className="pl-10"
            data-testid="search-products-input"
          />
        </div>
        <div className="rounded-lg border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Immagine</TableHead>
                <TableHead>Nome</TableHead>
                <TableHead>Categoria</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={4} className="text-center py-8">Caricamento...</TableCell></TableRow>
              ) : filteredProducts.length === 0 ? (
                <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">Nessun prodotto trovato</TableCell></TableRow>
              ) : (
                filteredProducts.map((product) => (
                  <TableRow key={product.id} data-testid={`product-row-${product.id}`}>
                    <TableCell>
                      <img src={product.image} alt={product.name} className="w-12 h-12 rounded-md object-cover" />
                    </TableCell>
                    <TableCell className="font-medium">{product.name}</TableCell>
                    <TableCell><Badge variant="secondary">{product.category}</Badge></TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end space-x-2">
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(product)} data-testid={`edit-product-${product.id}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(product.id)} className="text-destructive hover:text-destructive" data-testid={`delete-product-${product.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProductsTab;
